import { Injectable, inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '@/core/services/api/api.service';
import {
  ContrEvalSupResponse,
  OneShotEvaluationListeResponse,
  OneShotFluxTresoListeResponse,
  TransactionPrefinancerListeResponse,
  VenteSaisonnaliteListeResponse,
} from '../../interfaces/credit.interface';

@Injectable({ providedIn: 'root' })
export class CreditAnalyseService {
  private readonly api = inject(ApiService);
  private readonly endpoint = '/credit';

  // ── Activités ─────────────────────────────────────────────────────────────
  saveActivite(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveActiviteDmde', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteActivite(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteActivite/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Ventes mensuelles ─────────────────────────────────────────────────────
  saveVenteMensuelle(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveVenteMensuelle', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteVenteMensuelle(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteVenteMensuelle/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Ventes journalières ───────────────────────────────────────────────────
  saveVenteJournaliere(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveVenteJournaliere', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteVenteJournaliere(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteVenteJournaliere/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Achats mensuels ───────────────────────────────────────────────────────
  saveAchatMensuel(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveAchatMensuel', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteAchatMensuel(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteAchatMensuel/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Marge commerciale ─────────────────────────────────────────────────────
  saveMargeCommerciale(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveMargeCommerciale', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  updateMargeCommerciale(id: number, data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/updateMargeCom/' + id, data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteMargeCommerciale(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteMargeCom/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Actif circulant / Stock ───────────────────────────────────────────────
  saveActifCirculantStock(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveActifCirculantStock', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteActifCirculantStock(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteActifCirculantStock/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveStock(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveStock', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteStock(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteStock/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // deleteStock(id: number) {
  //   return this.api
  //     .delete<{ status: number }>(this.endpoint + '/deleteStock/' + id)
  //     .pipe(catchError((err) => throwError(() => err)));
  // }

  // ── Charges d'exploitation ────────────────────────────────────────────────
  getListeTypeCharge() {
    return this.api
      .get<{
        crTypeCharges: { id: number; libelle?: string }[];
      }>(this.endpoint + '/listeTypeCharge')
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveChargeExploitation(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveChargeExploitation', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveImprevuChargeExploitation(data: {
    activite?: number | null;
    imprevu?: number | null;
    refDemande?: string;
    charge?: number | null;
    montant?: number | null;
    commentaire?: string;
    chargeExploitation?: number | null;
  }) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveImprevus', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveImprevuChargeFamilial(data: { imprevu?: number | null; refDemande?: string }) {
    const payload = {
      imprevu: data.imprevu ?? null,
      demande: data.refDemande ?? null,
    };
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveImprevusChFam', payload)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteChargeExploitation(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteChargeExploitation/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Trésorerie ────────────────────────────────────────────────────────────
  saveTresorerie(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveTresorerie', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteTresorerie(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteTresorerie/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // deleteTresorerie2(id: number) {
  //   return this.api
  //     .delete<{ status: number }>(this.endpoint + '/deleteTresorerie/' + id)
  //     .pipe(catchError((err) => throwError(() => err)));
  // }

  // ── Créances clients ──────────────────────────────────────────────────────
  saveCreanceClient(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveCreanceClient', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  updateCreanceClient(id: number, data: Record<string, unknown>) {
    return this.api
      .post<{
        status: number;
        message?: string;
      }>(this.endpoint + '/saveCreanceClient', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteCreanceClient(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteCreanceClient/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Avances fournisseurs ──────────────────────────────────────────────────
  saveAvanceFournisseur(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveAvanceFournisseur', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  updateAvanceFournisseur(id: number, data: Record<string, unknown>) {
    return this.api
      .post<{
        status: number;
        message?: string;
      }>(this.endpoint + '/saveAvanceFournisseur', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteAvanceFournisseur(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteAvanceFournisseur/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Dettes fournisseurs ───────────────────────────────────────────────────
  saveDetteFournisseur(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveDetteFournisseur', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  updateDetteFournisseur(id: number, data: Record<string, unknown>) {
    return this.api
      .post<{
        status: number;
        message?: string;
      }>(this.endpoint + '/saveDetteFournisseur', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteDetteFournisseur(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteDette', {
        type: 'DetteFournisseur',
        element: id,
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Dettes entreprise ──────────────────deleteDette───────────────────────────────────
  saveDetteEntreprise(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveDetteEntreprise', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  updateDetteEntreprise(id: number, data: Record<string, unknown>) {
    return this.api
      .post<{
        status: number;
        message?: string;
      }>(this.endpoint + '/saveDetteEntreprise', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteDetteEntreprise(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteDette', {
        type: 'DetteEntreprise',
        element: id,
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Profil entrepreneur ───────────────────────────────────────────────────
  saveProfilEntrepreneur(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveProfilEnt', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Profil familial ───────────────────────────────────────────────────────
  saveTresorerieFamille(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveTresorerieFamille', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteTresorerieFamille(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteTresorerieFamille/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveChargeFamille(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveChargeFamille', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteChargeFamille(id: number | string) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteChargeFamille/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveProfilFamilial(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveProfilFamilial', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveMembreMenage(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveMenageRevenuFamille', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteMembreMenage(id: number) {
    return this.api
      .delete<{ status: number }>(this.endpoint + '/deleteMenageRevenuFamille/' + id)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Crédits antérieurs ────────────────────────────────────────────────────
  /** Legacy parity: /saveCrediAntDem */
  saveCreditAnterieur(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveCrediAntDem', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Legacy parity: /saveCrediAntAct */
  saveCreditAnterieurActivite(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveCrediAntAct', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Évaluation prêt one shot — partenaires (clients / fournisseurs) ─────────
  /**
   * Enregistre (ou modifie) une ligne « evaluationPretOneShot ».
   * `type` : 0 = Client, 1 = Fournisseur.
   * Pour modifier, fournir l'id de la ligne dans `evaluationPretOneShot`.
   */
  saveEvaluationOneShot(data: {
    refDemande: string;
    type: 0 | 1;
    nom: string;
    ca: number;
    evaluationPretOneShot?: string | number;
  }) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveEvaluationOneShot', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Liste des évaluations one shot (partenaires) enregistrées pour une demande. */
  getEvaluationOneShotListe(refDemande: string) {
    return this.api
      .get<OneShotEvaluationListeResponse>(
        this.endpoint + '/evaluationOneShotListe/' + refDemande,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Supprime une ligne « evaluationPretOneShot » via sa référence. */
  deleteEvaluationOneShot(id: string | number) {
    return this.api
      .delete<{ status: number; message?: string }>(
        this.endpoint + '/deleteEvaluationOneShot/' + id,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Évaluation prêt one shot — flux / trésorerie ───────────────────────────
  /**
   * Enregistre (ou modifie) un flux de trésorerie one shot.
   * institution : 0=CA, 1=Autre, 2=Commanditaire. type : 0=Espèce, 1=Virement.
   * sens : 1=Entrant, 2=Sortant. Pour modifier : `evaluationPretOneShot` = id.
   */
  saveEvaluationOneShotFlux(data: {
    refDemande: string;
    activite: number;
    type: number;
    institution: number;
    montant: number;
    sens: number;
    mois: string;
    evaluationPretOneShot?: number;
  }) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveEvaluationOneShotFlux', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Liste des flux/trésorerie one shot enregistrés pour une demande. */
  getFluxTresoList(refDemande: string) {
    return this.api
      .get<OneShotFluxTresoListeResponse>(this.endpoint + '/FluxTresoList/' + refDemande)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Supprime un flux/trésorerie one shot via son id. */
  deleteEvalOneShotFluxTreso(id: string | number) {
    return this.api
      .delete<{ status: number; message?: string }>(
        this.endpoint + '/deleteEvalOneShotFluxTreso/' + id,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Évaluation prêt one shot — transaction à préfinancer ───────────────────
  /**
   * Enregistre (ou modifie) la transaction à préfinancer.
   * modaliteReglement : 1=Chèque, 2=Espèce, 3=Virement, 4=Autres.
   * factureNormalisee : 0=Non, 1=Oui. Pour modifier : `evaluationPretOneShot` = id.
   */
  saveTransactionPrefinancer(data: {
    refDemande: string;
    activite: number;
    montantMarche: number;
    avanceRecue: number;
    soldeArecevoir: number;
    dateReglementPrevue: string;
    modaliteReglement: number;
    factureNormalisee: number;
    numeroFacture: string;
    dateEmission: string;
    referenceAttestation: string;
    description: string;
    compteCa: number;
    compteAutreInstitution: number;
    observations: string;
    evaluationPretOneShot?: number;
  }) {
    return this.api
      .post<{ status: number; message?: string }>(
        this.endpoint + '/saveTransactionPrefinancer',
        data,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Infos de la transaction à préfinancer enregistrée pour une demande. */
  getInfosTransactionPrefinancer(refDemande: string) {
    return this.api
      .get<TransactionPrefinancerListeResponse>(
        this.endpoint + '/infosTransactionPrefinancer/' + refDemande,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Fiche de contre-évaluation digitalisée ─────────────────────────────────
  /**
   * Enregistre (ou modifie) une ligne de la fiche de contre-évaluation.
   * Pour modifier, fournir l'id de la ligne dans `contreEvaluation`.
   */
  saveCrContreEvaluation(data: {
    refDemande: string;
    activite: number;
    typeDonnee: string;
    infoCollecte: number | string;
    commentaire: string;
    contreEvaluation?: number;
  }) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveCrContreEvaluation', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Liste des lignes de contre-évaluation enregistrées pour une demande. */
  getContrEvalSup(refDemande: string) {
    return this.api
      .get<ContrEvalSupResponse>(this.endpoint + '/contrEvalSup/' + refDemande)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Références familiales ─────────────────────────────────────────────────
  saveReferenceFamiliale(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveReferenceFamille', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getReferenceFamilialeListe(refDemande: string) {
    return this.api
      .get<{ statut: number; evaluations: unknown[] }>(
        this.endpoint + '/referenceFamilleListe/' + refDemande,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Références commerciales ───────────────────────────────────────────────
  saveReferenceCommerciale(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveReferenceCom', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getReferenceCommercialeListe(refDemande: string) {
    return this.api
      .get<{ statut: number; evaluations: unknown[] }>(
        this.endpoint + '/referenceComListe/' + refDemande,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Fiche de production ───────────────────────────────────────────────────
  saveFicheProd(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveFicheProd', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getFicheProdListe(refDemande: string) {
    return this.api
      .get<{ statut: number; listeficheProduction: unknown[] }>(
        this.endpoint + '/ficheProd/' + refDemande,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  saveCrFicheProdMp(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveCrFicheProdMp', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getListeFicheProdMp(ficheProdId: number) {
    return this.api
      .get<{ statut: number; listeficheProduction: unknown[] }>(
        this.endpoint + '/listeFicheProdMp/' + ficheProdId,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Récap ventes ─────────────────────────────────────────────────────────
  saveRecapVente(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveRecapVente', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getRecapVenteListe(refDemande: string) {
    return this.api
      .get<{ statut: number; listeRecapVentes: unknown[] }>(
        this.endpoint + '/recapVente/' + refDemande,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Ventes saisonnalité ───────────────────────────────────────────────────
  saveVenteSaisonnalite(data: Record<string, unknown>) {
    return this.api
      .post<{ status: number; message?: string }>(this.endpoint + '/saveVenteSaisonalite', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getVenteSaisonnaliteList(refDemande: string) {
    return this.api
      .get<VenteSaisonnaliteListeResponse>(
        this.endpoint + '/venteSaisonaliteListe/' + refDemande,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  //--- Suppimer un demande ----------------------------
  deleteDemande(idDemande: string) {
    return this.api
      .delete<{ status: number; message?: string }>(
        this.endpoint + '/deleteDme/' + idDemande,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }
}
